import anime from 'animejs';
import Branch from './Branch';
import { icons } from './icons';
import { mapBox, geocoder, nav, createMarker } from './mapbox';
import config from './mapConfig';
import { formatData } from './helpers';
import { createFocusTrap } from 'focus-trap';
import { prefersReducedMotion } from '../../scripts/ada';

const locations = formatData(window.mapData.locations);

//////////////////////
// Initialize

const $window = $(window);
const $body = $('body');
const device = {
  iOS: !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform),
  android: /(android)/i.test(navigator.userAgent),
  ie11: !!window.MSInputMethodContext && !!document.documentMode
};

device.isMobile = device.iOS || device.android;

if (device.isMobile) {
  $body.addClass('is-mobile');
} else {
  $body.addClass('is-desktop');
}

if (device.ie11) {
  $body.addClass('ie11');
}

const wrapper = $('#locations');
const header = $('#header');
const introContent = $('#locations-intro');
const listWrapper = $('#locations-list-wrapper');
const mapBar = $('#map-bar');
const list = $('#locations-list');
const mapWrapper = $('#map-wrapper');
const closeButton = $('#map-close');
const branchAnimationWrapper = $('#branch-animation-wrapper');
const branchContent = $('#branch-content');
const branchWindowWrapper = $('#branch-window-wrapper');
const branchWindow = $('#branch-window');
const branchPhotoWrapper = $('#branch-photo-wrapper');
const tabWrapper = $('#view-tabs-wrapper');
const tabs = tabWrapper.find('.tab');
const clickEventName = device.isMobile ? 'touchstart' : 'click';
const userLocationWrapper = $('#user-location');
const input = geocoder._inputEl;

let userLocation = null;
let branchClone = null;
let currentView = 'details';

// Window size variable used for displaying map
// differently at different screen sizes
let screenSize;

function setScreenSize() {
  const vh = $window.innerHeight() * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);

  const vw = $window.innerWidth() * 0.01;
  document.documentElement.style.setProperty('--vw', `${vw}px`);

  const width = $window.outerWidth();

  if (width > 1350) {
    screenSize = 'xLarge';
  } else if (width > 935) {
    screenSize = 'large';
  } else if (width > 720) {
    screenSize = 'medium';
  } else {
    screenSize = 'small';
  }
}

setScreenSize();

function isMobileSize() {
  return screenSize === 'small';
}

const setPhotoWrapperPosition = (() => {
  let prevScreenSize = null;

  return () => {
    if (isMobileSize()) {
      branchPhotoWrapper.find('.photo-wrapper-info').show();

      if (screenSize !== prevScreenSize) {
        branchPhotoWrapper.detach().prependTo('#map-animation-wrapper');
      }

      branchPhotoWrapper.removeClass('no-photo-desktop');
    } else {
      branchPhotoWrapper.find('.photo-wrapper-info').hide();

      if (screenSize !== prevScreenSize) {
        branchPhotoWrapper.detach().insertAfter('#main-info');
      }

      if (branches.current && !branches.current.imgUrl) {
        branchPhotoWrapper.addClass('no-photo-desktop');
      }
    }

    prevScreenSize = screenSize;
  };
})();

// Position map element inside sidebar on  mobile
const positionMapElement = (function () {
  let isInSidebar = false;

  function position() {
    if (isMobileSize()) {
      if (!isInSidebar) {
        isInSidebar = true;
        setPhotoWrapperPosition();
        mapBox.removeControl(nav);
        mapBox.resize();
      }
    } else {
      if (isInSidebar) {
        isInSidebar = false;
        setPhotoWrapperPosition();
        mapBox.addControl(nav, 'bottom-right');
        mapBox.resize();
      }
    }
  }

  return position;
})();

positionMapElement();

const elementData = {
  branchWindowPadding: 193
};

function setElementData() {
  elementData.wrapperHeight = wrapper.outerHeight();
  elementData.headerHeight = header.outerHeight();
  elementData.wrapperOffset = wrapper.offset();
  elementData.topPosition = Math.round(
    elementData.wrapperOffset.top + introContent.outerHeight() - elementData.headerHeight
  );
  elementData.windowHeight = $window.outerHeight();
  elementData.windowWidth = $window.outerWidth();
  elementData.listHeight = listWrapper.outerHeight();
  elementData.listTopPosition = listWrapper.offset().top;
  elementData.bottomPosition =
    elementData.topPosition + Math.max(elementData.windowHeight, elementData.listHeight);
  elementData.barHeight = $('#map-bar').outerHeight();
}

let branchContentOverflow = false;

function setBranchContentOverflow() {
  if (!branches.current) {
    branchContentOverflow = false;
  } else {
    let branchWindowHeight = branchWindow[0].offsetHeight;
    let maxHeight = elementData.windowHeight - elementData.headerHeight - 90;

    if (isMobileSize()) {
      branchWindowHeight += 150;
      maxHeight -= $('#view-tabs-wrapper').outerHeight();
    }

    branchContentOverflow = branchWindowHeight >= maxHeight;
  }
}

setElementData();

let resizeTimer = null;

$window.on('resize', () => {
  clearTimeout(resizeTimer);
  wrapper.addClass('resizing');

  resizeTimer = setTimeout(() => {
    setElementData();
    setBranchContentOverflow();

    let prevScreenSize = screenSize;
    setScreenSize();

    if (prevScreenSize === screenSize) {
      wrapper.removeClass('resizing');
      return;
    }

    if (branches.current) {
      lockFocus(isMobileMap() ? focusTraps.map : focusTraps.details);
    } else {
      if (isMobileMap()) {
        lockFocus(focusTraps.map);
      } else {
        unlockFocus();
      }
    }

    if (isMobileSize()) {
      mapWrapper.toggleClass('hidden', currentView !== 'map');
    } else {
      mapWrapper.removeClass('hidden');
    }

    setMapboxZoom();
    positionMapElement();
    wrapper.removeClass('resizing');
  }, 70);
});

if (window.visualViewport && device.isMobile) {
  const baseHeight = visualViewport.height;

  const updateBranchWindowPadding = () => {
    const newHeight = visualViewport.height;

    if (newHeight >= baseHeight) {
      const adjustment = newHeight - baseHeight;
      const padding = Math.max(elementData.branchWindowPadding - adjustment, 115);

      branchWindowWrapper.css('padding-bottom', padding);
    }
  };

  let viewportResizeTimer = setTimeout(updateBranchWindowPadding, 200);

  visualViewport.addEventListener('resize', () => {
    clearTimeout(viewportResizeTimer);
    viewportResizeTimer = setTimeout(updateBranchWindowPadding, 50);
  });
}

function setMapboxZoom() {
  if (branches.current) return;
  if (screenSize === 'medium' || isMobileSize()) {
    mapBox.setZoom(config.zoomMobile);
  } else {
    mapBox.setZoom(config.zoom);
  }
}

//////////////////////
// Keyboard Focus

let currentFocusTrap = null;

const sharedTrapElements = [mapBar[0], '#map-close-wrapper'];
const opts = { allowOutsideClick: true, initialFocus: branchWindow[0] };

// prettier-ignore
const focusTraps = {
  desktop: createFocusTrap([...sharedTrapElements, branchWindow[0], mapWrapper[0]], opts),
  details: createFocusTrap([...sharedTrapElements, branchWindow[0], tabWrapper[0]], opts),
  map: createFocusTrap([...sharedTrapElements, mapWrapper[0], tabWrapper[0]], {
    ...opts,
    initialFocus: mapBar[0]
  })
};

$window.on('show.bs.modal globalsearchopen mobilemenuopen', () => {
  currentFocusTrap?.deactivate();
});

$window.on('hide.bs.modal globalsearchclose mobilemenuclose', () => {
  currentFocusTrap?.activate();
});

function lockFocus(trap) {
  if (currentFocusTrap && currentFocusTrap !== trap) {
    currentFocusTrap.deactivate();
  }

  trap.activate();
  currentFocusTrap = trap;
}

function unlockFocus() {
  currentFocusTrap?.deactivate();
  currentFocusTrap = null;
}

// Prevent focusing map on mobile when not visible
let mapHideTimer = null;

mapWrapper.on('transitionstart', () => {
  clearTimeout(mapHideTimer);
  mapWrapper.removeClass('hidden').addClass('animating');
});

mapWrapper.on('transitionend', () => {
  mapWrapper.removeClass('animating');

  mapHideTimer = setTimeout(() => {
    if (isMobileSize() && currentView !== 'map') {
      mapWrapper.addClass('hidden');
    }
  }, 50);
});

//////////////////////
// Scrolling

// Only allow touch manipulation of map
// when scrolled down to main section
let touchDisabled = true;

$window.on('scroll', () => {
  const scrollAmount = $window.scrollTop();

  if (scrollAmount >= elementData.topPosition && touchDisabled) {
    touchDisabled = false;
    $body.removeClass('disable-map-pan');
  } else if (scrollAmount < elementData.topPosition && !touchDisabled) {
    touchDisabled = true;
    $body.addClass('disable-map-pan');
  }
});

$body.addClass('disable-map-pan');

// Add position: sticky polyfill as it is
// not supported in older browsers
const positionStickySupport = (function () {
  const el = document.createElement('a');
  const mStyle = el.style;
  mStyle.cssText = 'position:sticky;position:-webkit-sticky;position:-ms-sticky;';
  return mStyle.position.indexOf('sticky') !== -1;
})();

if (!positionStickySupport) {
  wrapper.addClass('no-sticky-support');

  // Use position fixed instead of sticky
  let isSticky = false;

  const checkStickyScroll = () => {
    const scrollAmount = $window.scrollTop();

    if (!isSticky && scrollAmount >= elementData.topPosition) {
      isSticky = true;
      wrapper.addClass('sticky-active');
    } else if (isSticky) {
      if (scrollAmount < elementData.topPosition) {
        isSticky = false;
        wrapper.removeClass('sticky-active sticky-stop');
      } else if (scrollAmount + elementData.windowHeight >= elementData.bottomPosition) {
        wrapper.addClass('sticky-stop');
      } else {
        wrapper.removeClass('sticky-stop');
      }
    }
  };

  checkStickyScroll();
  $window.on('scroll', checkStickyScroll);
}

const scrollbarWidth = (function () {
  // Creating invisible container
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll'; // forcing scrollbar to appear
  outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps
  document.body.appendChild(outer);

  // Creating inner element and placing it in the container
  const inner = document.createElement('div');
  outer.appendChild(inner);

  // Calculating difference between container's full width and the child width
  const width = outer.offsetWidth - inner.offsetWidth;

  // Removing temporary elements from the DOM
  outer.parentNode.removeChild(outer);

  return width;
})();

let scrollingDisabled = false;

function disableScroll() {
  scrollingDisabled = true;
  isFixed = true;
}

function enableScroll() {
  scrollingDisabled = false;
  isFixed = false;
}

['touchmove', 'wheel'].forEach(name => {
  document.addEventListener(
    name,
    e => {
      if (scrollingDisabled) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    { passive: false }
  );
});

// Scroll window when using wheel on map view
wrapper[0].addEventListener(
  'wheel',
  e => {
    if (isMobileSize() && screenSize === 'medium') return;

    if (branchAnimation || windowAnimation || scrollingDisabled || scrollAnimation) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    if (!branches.current || !branches.streetViewActive) return;

    e.preventDefault();
    e.stopImmediatePropagation();
  },
  { passive: false }
);

const touchPosition = { x: 0, y: 0 };
const canScrollBranchWindow = () => (isFixed || branchAnimation) && branchContentOverflow;

branchWindowWrapper[0].addEventListener(
  'touchstart',
  e => {
    const { pageX = 0, pageY = 0 } = e.touches[0] ?? {};

    touchPosition.x = pageX;
    touchPosition.y = pageY;
  },
  { useCapture: true, passive: false }
);

branchWindowWrapper[0].addEventListener(
  'touchmove',
  e => {
    if (canScrollBranchWindow()) {
      e.stopImmediatePropagation();

      if (e.touches[0]) {
        const { pageX = 0, pageY = 0 } = e.touches[0];

        if (branchWindowWrapper.scrollTop() <= 0 && pageY > touchPosition.y) {
          e.preventDefault();
        }

        touchPosition.x = pageX;
        touchPosition.y = pageY;
      }
    }
  },
  { useCapture: true, passive: false }
);

branchWindowWrapper[0].addEventListener(
  'wheel',
  e => {
    if (canScrollBranchWindow()) {
      e.stopImmediatePropagation();

      const scrollAmount = branchWindowWrapper.scrollTop();
      const maxScroll =
        branchWindowWrapper[0].scrollHeight - branchWindowWrapper.outerHeight();

      const overscrollUp = scrollAmount <= 0 && e.deltaY < 0;
      const overscrollDown = scrollAmount >= maxScroll && e.deltaY > 0;

      if (overscrollUp || overscrollDown) {
        e.preventDefault();
        branchWindowWrapper.scrollTop(overscrollUp ? 0 : maxScroll);
      }
    }
  },
  { useCapture: true, passive: false }
);

mapWrapper[0].addEventListener(
  'wheel',
  e => {
    if (
      e.deltaY &&
      isFixed &&
      !windowAnimation &&
      branchContentOverflow &&
      screenSize !== 'small'
    ) {
      branchWindowWrapper[0].scrollBy(0, e.deltaY);
    }
  },
  { passive: true }
);

class BranchList {
  constructor(locations) {
    this.current = null;
    this.prev = null;
    this.ignoreAnimations = false;
    this.ignoreAnimationsTimer = null;
    this.list = [];

    locations.forEach(location => {
      this.list.push(new Branch(location, this));
    });

    // Default to sorting alphabetically if
    // the user hasn't entered their location
    this.sort();
    setElementData();

    // Deselect when close button clicked
    branchContent.on('click', '.close', () => {
      this.deselect();
    });

    branchWindowWrapper.on('click', e => {
      if (
        branches.current &&
        screenSize !== 'small' &&
        e.target === branchWindowWrapper[0]
      ) {
        this.deselect();
      }
    });

    closeButton.on(clickEventName, () => {
      this.deselect();
    });

    // Deselect when enter key pressed
    if (!device.isMobile) {
      $(document).on('keydown', e => {
        if (this.current && (e.key === 'Escape' || e.key === 'Esc')) {
          this.deselect();
        }
      });
    }
  }

  sort(useDistance) {
    if (useDistance) {
      // Sort by distance to user's location
      this.list.sort((a, b) => {
        return a.distance - b.distance;
      });
    } else {
      // Sort by branch name alphabetically
      this.list.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();

        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
    }

    this.update();
  }

  update() {
    let branchElements = $([]);

    this.list.forEach(branch => {
      if (!branchClone) {
        branchClone = branch.element
          .clone()
          .removeAttr('role')
          .attr('tabindex', -1)
          .addClass('clone')
          .append(
            `
            <button class="close">
              <span class="sr-only">Close</span>
            </button>
          `
          )
          .prependTo($('#branch-positioner'));
      }

      const element = branch.element.detach();
      branchElements = branchElements.add(element);
    });

    list.empty().append(branchElements);
    this.elements = branchElements;
  }

  // Select element by clicking either marker or list item
  select(branch, e) {
    if (
      !branch ||
      this.current === branch ||
      (!this.ignoreAnimations && (branchAnimation || windowAnimation))
    )
      return;
    clearTimeout(this.ignoreAnimationsTimer);
    this.ignoreAnimations = false;

    this.ignoreAnimationsTimer = setTimeout(() => {
      this.ignoreAnimations = true;
    }, 2500);

    this.prev = this.current;

    if (this.prev) {
      this.prev.marker.removeClass('current-marker');
    }

    this.current = branch;
    branches.elements.removeClass('selected hidden');

    setBranchDetails(branch);

    closeButton.attr({
      tabindex: 0,
      'aria-hidden': false
    });

    const trap =
      screenSize !== 'small'
        ? focusTraps.desktop
        : currentView === 'map'
        ? focusTraps.map
        : focusTraps.details;

    lockFocus(trap);

    if (branch instanceof Branch) {
      branches.mapUpdated = false;

      if (!this.prev) {
        wrapper.addClass('selected');
        openBranch(branch, e);
      } else {
        zoomMapToBranch(branch);
      }
    }
  }

  // Hide map window and zoom out from branch
  deselect(preventZoom) {
    if (
      !this.ignoreAnimations &&
      (branchAnimation || windowAnimation || scrollAnimation)
    ) {
      return;
    }

    this.ignoreAnimations = false;

    if (this.current) {
      this.current.deselect();
    }

    currentView = 'details';

    unlockFocus();
    closeBranchWindow(preventZoom, this.current);

    closeButton.attr({
      tabindex: -1,
      'aria-hidden': true
    });

    this.current = null;
  }

  searchLocation(coords) {
    this.setDistance(coords);
    this.sort(true);

    if (!this.list.length) return;

    let selectedBranch = this.list[0];

    // Don't select an ATM only branch
    if (selectedBranch.atmOnly && this.list.length > 1) {
      for (let i = 1; i < this.list.length; i++) {
        const branch = this.list[i];
        if (!branch.atmOnly) {
          selectedBranch = branch;
          break;
        }
      }
    }

    $('#location-input-wrapper').addClass('location-found');
    selectedBranch.select();
  }

  setDistance(coords) {
    this.list.forEach(branch => {
      const distance = getDistance(branch.coords, coords);
      branch.distance = distance;
    });
  }

  removeDistance() {
    this.list.forEach(branch => {
      branch.distance = null;
    });

    $('#location-input-wrapper').removeClass('location-found');
  }
}

// Manages all branches
const branches = new BranchList(locations);

addMarkersToMap(branches);

//////////////////////////////
// Geocoder

geocoder.on('result', e => {
  const [lng, lat] = e.result.center;
  const coords = { lat, lng };
  branches.searchLocation(coords);
});

//////////////////////
// Map Markers

// Add a marker for each branch
async function addMarkersToMap(branches) {
  branches.list
    .slice()
    .sort((a, b) => b.coords?.lng ?? 0 - a.coords?.lng ?? 0)
    .forEach(branch => {
      if (branch.coords) {
        createMarker(branch, branches);
      } else {
        geocode(encodeURIComponent(formatAddress(branch.address)), coords => {
          if (coords) {
            branch.coords = coords;
            createMarker(branch, branches);
          }
        });
      }
    });
}

//////////////////////
// Control Map Position

function isMobileMap() {
  return isMobileSize() && currentView === 'map';
}

function isMobileDetails() {
  return isMobileSize() && currentView !== 'map';
}

function setMapPosition(position) {
  if (prefersReducedMotion() || isMobileDetails()) {
    mapBox.jumpTo(position);
  } else {
    mapBox.flyTo(position);
  }
}

// Zoom in on a marker
function zoomIn({ coords }) {
  setMapPosition({
    center: [coords.lng, coords.lat],
    zoom: config.zoomClose,
    pitch: config.pitchClose,
    speed: config.zoomSpeed
  });
}

// Zoom back out to view all markers
function zoomOut() {
  setMapPosition({
    center: config.center,
    zoom: screenSize === 'medium' || isMobileSize() ? config.zoomMobile : config.zoom,
    pitch: config.pitch,
    speed: config.zoomOutSpeed
  });
}

//////////////////////
// Location View Tabs

tabs.on(clickEventName, function () {
  const tab = $(this);

  if (!tab.hasClass('selected')) {
    const name = tab.attr('data-tab');
    currentView = name;

    setupScrollAnimation(() => {
      tabs.removeClass('selected');
      tab.addClass('selected');
      wrapper.removeClass('details map').addClass(name);
    });

    if (branches.current) {
      lockFocus(isMobileMap() ? focusTraps.map : focusTraps.details);
    } else {
      if (name === 'map') {
        disableScroll();

        if (isMobileMap()) {
          lockFocus(focusTraps.map);
        }
      } else {
        enableScroll();
        unlockFocus();
      }
    }

    scrollAnimation.play();
  }
});

function initSentinels() {
  const bottomSentinel = document.querySelector('.sentinel-bottom');
  let hasInitialPosition = false;

  if (bottomSentinel) {
    const observer = new IntersectionObserver(([entry]) => {
      const {
        isIntersecting,
        boundingClientRect: { y }
      } = entry;

      tabWrapper.toggleClass('position-absolute', isIntersecting || y < 0);

      if (!hasInitialPosition) {
        tabWrapper.removeClass('initializing');
        hasInitialPosition = true;
      }
    });

    observer.observe(bottomSentinel);
  } else {
    tabWrapper.removeClass('initializing');
    hasInitialPosition = true;
  }
}

if (document.readyState === 'complete') {
  initSentinels();
} else {
  document.addEventListener('DOMContentLoaded', initSentinels);
}

//////////////////////
// Tooltips

function positionTooltip(tooltip, parent) {
  const tooltipWidth = 146;
  const windowPadding = 8;
  const right = parent.offset().left + parent.outerWidth() / 2 + tooltipWidth / 2;
  const overlap = parseInt(elementData.windowWidth - right - windowPadding);

  if (overlap < 0) {
    tooltip.css({
      marginLeft: overlap
    });

    tooltip.find('.triangle').css({
      marginLeft: -overlap
    });
  }

  tooltip.css({
    display: 'block',
    width: tooltipWidth
  });
}

$('.location-tooltip').each(function () {
  const tooltip = $(this).prepend('<div class="triangle"></div>');
  const parent = tooltip.parent();

  parent.on('mouseenter', () => {
    positionTooltip(tooltip, parent);
    tooltip.addClass('visible');
  });

  parent.on('mouseleave', () => {
    tooltip.removeClass('visible');
    tooltip.removeAttr('style');
    tooltip.find('.triangle').removeAttr('style');
  });
});

//////////////////////
// Geolocation

if ('geolocation' in navigator) {
  userLocationWrapper.on('click', getUserLocation);
} else {
  userLocationWrapper.remove();
}

// Try to get the user's location using the geolocation API
let isGettingLocation = false;

function getUserLocation() {
  if (isGettingLocation) {
    return;
  }

  function selectClosestLocation() {
    if (navigator.geolocation) {
      isGettingLocation = true;
      userLocationWrapper.addClass('loading-spinner');

      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          const { latitude: lat, longitude: lng } = coords;
          userLocation = { lat, lng };
          branches.searchLocation(userLocation);

          reverseGeocode(userLocation, address => {
            if (address) {
              input.value = address;
            }

            userLocationWrapper.removeClass('loading-spinner');
            isGettingLocation = false;
          });
        },
        err => {
          console.warn(err);

          let str =
            'Unable to get location. Please ensure you have location services turned on.';

          if (device.iOS) {
            str += '\n\nSettings > Privacy > Location Services > Safari';
          }

          isGettingLocation = false;
          userLocationWrapper.removeClass('loading-spinner');

          alert(str);
        }
      );
    }
  }

  if (document.readyState === 'complete') {
    selectClosestLocation();
  } else {
    document.addEventListener('DOMContentLoaded', selectClosestLocation);
  }
}

//////////////////////
// Branch Select

let branchAnimation = null;
let windowAnimation = null;

function setupClone(branch, e) {
  // Add a second clone of the branch to act as a
  // placeholder element as we animate the window scroll
  const scrollAmount = window.scrollY;
  const branchRect = branch.element[0].getBoundingClientRect();
  const branchOffset = {
    top: branchRect.top + scrollAmount,
    left: branchRect.left
  };

  // Position the clone element that will be animated to the
  // expanded branch details window
  const contentRect = branchContent[0].getBoundingClientRect();
  const contentOffset = {
    top: contentRect.top + scrollAmount,
    left: contentRect.left
  };

  let translate = branchOffset.top - contentOffset.top - 15;

  const translateStyle = `translateY(${translate}px)`;

  branchClone.css({
    transform: translateStyle,
    opacity: 1,
    width: branch.element[0].offsetWidth
  });

  branch.element.addClass('hidden');

  if (!prefersReducedMotion()) {
    addRipple(e, 900, branchClone, branchOffset, true);
  }

  return translate;
}

function setupBranchAnimation(branch, delay = 0, scrollDuration, cloneTransform) {
  const cloneHeight = branchClone[0].offsetHeight;
  const positionAdjustment = !isMobileSize()
    ? cloneHeight + elementData.barHeight + 15
    : cloneHeight + 15;

  if (device.ie11) {
    branchAnimationWrapper.css('top', positionAdjustment);
  } else {
    branchAnimationWrapper.css('transform', `translateY(${positionAdjustment}px)`);
  }

  const baseDuration = 650;
  const baseDistance = 350;
  const maxDuration = 700;
  const minDuration = 350;
  const normalizedDuration = (baseDuration * Math.abs(cloneTransform)) / baseDistance;
  const cloneDuration = Math.round(
    Math.max(minDuration, Math.min(maxDuration, normalizedDuration))
  );

  if (cloneTransform === 0) {
    branchAnimation = {
      play: () => {
        if (branchAnimation.hasStarted) return;
        branchAnimation.hasStarted = true;
        branchAnimation = null;
        wrapper.addClass('scroll-finished');
        branchClone.addClass('complete');

        if (prefersReducedMotion()) {
          windowAnimation.play();
        } else {
          setTimeout(windowAnimation.play, Math.max(scrollDuration * 0.7, 250));
        }

        setTimeout(() => {
          if (isMobileSize()) {
            zoomMapToBranch(branch);
          }
        }, 300);
      }
    };
  } else {
    const start = () => {
      if (branchAnimation.hasStarted) return;
      branchAnimation.hasStarted = true;
      wrapper.addClass('scroll-finished');

      const styles = {
        opacity: 1,
        transitionDuration: `${cloneDuration}ms`,
        transform: 'translate3d(0, 0, 0)'
      };

      setTimeout(branchAnimation.complete, cloneDuration * 0.8);

      if (scrollbarWidth) {
        styles.width = branchWindow[0].offsetWidth;
      }

      branchClone.css(styles);
    };

    branchAnimation = {
      play: () => {
        if (delay) {
          setTimeout(start, delay);
        } else {
          requestAnimationFrame(start);
        }
      },

      complete: () => {
        branchClone.addClass('complete');
        windowAnimation.play();
        branchAnimation = null;
      }
    };
  }

  windowAnimation = {
    play() {
      branchWindowWrapper.css({ opacity: 1, visibility: 'visible' });
      branchWindow.one('transitionend', windowAnimation.complete);
      branchWindow.addClass('open');
    },

    complete() {
      branchClone.prependTo(branchWindow);
      branchWindowWrapper.addClass('complete');

      if (!device.ie11) {
        branchAnimationWrapper.removeAttr('style');
      }

      setTimeout(() => {
        zoomMapToBranch(branch);
      }, 10);

      windowAnimation = null;

      setTimeout(setBranchContentOverflow, 0);

      requestIdleCallback(() => {
        $('.ripple-container').remove();
      });
    }
  };
}

let scrollAnimation = null;
let scrollOffset = 0;
let isFixed = false;

function openBranch(branch, e) {
  clearSelection();

  if (branches.markerClicked) {
    zoomMapToBranch(branch);
  }

  // setupScrollAnimation();
  const cloneTransform = setupClone(branch, e);

  disableScroll();

  const scrollDuration = setupScrollAnimation();
  setupBranchAnimation(branch, 10, scrollDuration, cloneTransform);

  scrollAnimation?.play();

  if (!prefersReducedMotion()) {
    branchAnimation?.play();

    setTimeout(() => {
      zoomMapToBranch(branch);
    }, 10);
  }
}

function zoomMapToBranch(branch) {
  if (!branches.mapUpdated) {
    zoomIn(branch);
    branches.mapUpdated = true;
    branches.markerClicked = false;
  }
}

function setupScrollAnimation(callback) {
  const { topPosition } = elementData;
  let newScrollPosition = null;

  const props = {
    scrollAmount: window.scrollY
  };

  // Scroll down to content
  if (props.scrollAmount < topPosition) {
    newScrollPosition = topPosition;
  } else {
    const { windowHeight, wrapperOffset, wrapperHeight } = elementData;
    const windowBottom = props.scrollAmount + windowHeight;
    const wrapperBottom = wrapperOffset.top + wrapperHeight;

    if (windowBottom > wrapperBottom) {
      newScrollPosition = props.scrollAmount + (wrapperBottom - windowBottom);
    }
  }

  let hasStartedBranchAnimation = false;

  const playBranchAnimation = () => {
    if (branchAnimation && !hasStartedBranchAnimation) {
      hasStartedBranchAnimation = true;
      branchAnimation.play();
    }
  };

  if (newScrollPosition !== null) {
    if (newScrollPosition === 0) newScrollPosition = topPosition;

    scrollOffset = newScrollPosition - props.scrollAmount;

    const baseDuration = 700;
    const baseDistance = 400;
    const minDuration = 550;
    const maxDuration = 800;
    const normalizedDuration = (baseDuration * Math.abs(scrollOffset)) / baseDistance;
    const duration = Math.max(minDuration, Math.min(maxDuration, normalizedDuration));

    scrollAnimation = anime({
      targets: props,
      scrollAmount: newScrollPosition,
      elasticity: 0,
      duration: duration,
      autoplay: false,
      easing: 'cubicBezier(.56,.03,.22,1)',
      play: () => {
        setTimeout(playBranchAnimation, duration * 0.2);
      },
      update: () => {
        window.scrollTo(0, props.scrollAmount);
      },
      complete: () => {
        playBranchAnimation();
        callback?.();
        scrollAnimation = null;
      }
    });

    return duration;
  } else {
    scrollOffset = 0;

    scrollAnimation = {
      play: () => {
        playBranchAnimation();
        callback?.();
        scrollAnimation = null;
      }
    };

    return 0;
  }
}

function resetMap(preventZoom) {
  wrapper.removeClass('map-close');

  if (!branches.current && !preventZoom) {
    setTimeout(zoomOut, 50);
  }
}

function closeBranchWindow(preventZoom, branch) {
  branchAnimationWrapper.addClass('closing');
  wrapper.removeClass('selected map').addClass('details');
  tabs.removeClass('selected').first().addClass('selected');
  currentView = 'details';

  setTimeout(
    () => {
      resetMap(preventZoom, branch);
    },
    isMobileSize() ? 510 : 100
  );

  enableScroll();
  setTimeout(resetBranchDetails, 600);
}

// const branchTypeWrapper = branchWindow.find('.branch-type-wrapper');
const branchTypeWrapper = document.querySelector('.branch-type-wrapper');
const branchPhone = document.querySelector('#branch-details .phone');
const branchCallButtons = document.querySelectorAll('.call-branch');
const mobileBranchName = document.querySelector('.photo-wrapper-info .branch-name');
const branchPhoto = document.querySelector('#locations .photo');
const directionsLinks = document.querySelectorAll('#locations .view-map');

// Populate branch information
function setBranchDetails(branch) {
  if (!branches.current || !branch || branch === branches.prev) return;

  // Branch information
  setType(branch);
  setPhoneNumber(branch);
  setMapUrl(branch);
  setHours(branch);
  setDetailsUrl(branch);
  setPhotoWrapper(branch);

  if (branchClone) {
    branchClone[0].innerHTML =
      branch.element[0].innerHTML +
      `
      <button class="close">
        <span class="sr-only">Close</span>
      </button>
    `;
  }
}

function setType(branch) {
  if (!branches.prev || branches.prev.atmOnly !== branch.atmOnly) {
    branchTypeWrapper.innerHTML = branch.getTypeIcon();
  }
}

function setPhoneNumber(branch) {
  const html = !branch.phone
    ? ''
    : `${icons.phone} <span>Call:&nbsp;</span>${branch.phone}`;

  branchPhone.innerHTML = html;
  branchCallButtons.forEach(el => {
    el.innerHTML = html;
    el.href = branch.phone ? `tel:${branch.phone}` : 'tel:(800) 359-2265';
  });
}

function setPhotoWrapper(branch) {
  mobileBranchName.innerHTML = branch.name;
  setImage(branch);
}

function setImage(branch) {
  if (branch.imgUrl) {
    const url =
      isMobileSize() && branch.imgUrlMobile ? branch.imgUrlMobile : branch.imgUrl;

    branchPhoto.style.backgroundImage = `url("${url}")`;
    branchPhoto.style.display = 'block';
    branchPhotoWrapper.removeClass('no-photo-desktop');
  } else {
    branchPhoto.style.display = 'none';

    if (!isMobileSize()) {
      branchPhotoWrapper.addClass('no-photo-desktop');
    }
  }
}

// Link to native map application on mobile
// or the Google Maps website on desktop. We wait
// until now to query the Google Place URL to avoid
// additional API calls. If the URL isn't found by the
// time the user clicks, fallback to default URL
function setMapUrl(branch) {
  let url;

  if (branch.placeUrl.length && !device.iOS) {
    url = branch.placeUrl;
  } else {
    const query = encodeURIComponent(formatAddress(branch.address));
    const host = device.iOS ? 'apple' : 'google';

    url = `https://maps.${host}.com/?q=${query}`;
  }

  directionsLinks.forEach(el => (el.href = url));
}

const branchHoursWrapper = document.querySelector('#branch-hours');
const openStatusWrapper = document.querySelector('#locations .open-status');

function setHours(branch) {
  // Hide hours if this is not a physical branch
  if (branch.atmOnly || !branch.hours || !branch.hoursHtml) {
    branchContent.addClass('no-hours');
    return;
  } else {
    branchContent.removeClass('no-hours');
  }

  branchHoursWrapper.innerHTML = branch.hoursHtml;

  let openStatus = null;

  // It is surprisingly complicated to get the time in a specific
  // timezone if the user isn't already in that locale. To avoid
  // having to load in a large external library to just calculate
  // whether a branch is open or not, we will only show the
  // open status if the user is in the Pacific time zone.
  const today = new Date();
  const offset = today.getTimezoneOffset() / 60;
  const isPacific = offset === 7 || offset === 8;
  const currentHours = branch.hours[today.getDay()];

  if (currentHours && isPacific) {
    if (currentHours.open === false) {
      openStatus = 'Closed';
    } else {
      // Make sure to get leading zeros so that data format matches
      const minutes = (today.getMinutes() < 10 ? '0' : '') + today.getMinutes();
      const time = parseInt(`${today.getHours()}${minutes}`);

      if (!isNaN(time)) {
        if (time >= currentHours.open && time < currentHours.close) {
          openStatus = 'Open now';
        } else {
          openStatus = 'Closed';
        }
      }
    }
  }

  if (openStatus !== null) {
    openStatusWrapper.innerHTML = `${icons.clock} ${openStatus}`;
  } else {
    branchContent.addClass('no-hours');
  }
}

function setDetailsUrl(branch) {
  const button = $('.branch-buttons .learn-more');

  if (!branch.atmOnly && branch.url.length) {
    button.attr('href', branch.url).show();
  } else {
    button.hide();
  }
}

function resetBranchDetails() {
  scrollAnimation = null;
  windowAnimation = null;
  branchAnimation = null;

  branchWindowWrapper.removeClass('complete').removeAttr('style').scrollTop(0);
  branchWindow.removeAttr('style');
  branchContent.removeAttr('style');
  branchClone
    .removeAttr('style')
    .removeClass('complete')
    .detach()
    .prependTo($('#branch-positioner'));
  branchWindow.removeClass();
  wrapper.removeClass('map toggle-loading scroll-finished').addClass('details');
  currentView = 'details';
  branchAnimationWrapper.removeAttr('style').removeClass();
  branches.elements.removeClass('selected hidden');

  setTimeout(() => {
    tabs.removeClass('selected').first().addClass('selected');
  }, 200);
}

//////////////////////
// Helpers

async function geocode(address, callback) {
  const res = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${address}.json?country=US&access_token=${config.accessToken}`
  );
  if (res.ok) {
    const data = await res.json();
    if (data.features.length) {
      callback(data.features[0].center.reverse());
    } else {
      callback(null);
    }
  } else {
    callback(null);
  }
}

async function reverseGeocode(location, callback) {
  const { lat, lng } = location;
  const res = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?types=address&access_token=${config.accessToken}`
  );
  if (res.ok) {
    const data = await res.json();
    if (data.features.length) {
      callback(data.features[0].place_name);
    } else {
      callback(null);
    }
  } else {
    callback(null);
  }
}

function formatAddress(
  { line1, line2 = '', city = '', state = '', zip = '' },
  multiLine,
  mergeAddressLines
) {
  if (mergeAddressLines) {
    line1 = `${line1} ${line2}`;
    line2 = null;
  }

  if (city) city += ',';

  if (multiLine) {
    line1 += '<br>';
    if (line2) line2 += '<br>';
  }

  return [line1, line2, city, state, zip].join(' ').replace('  ', ' ');
}

// Returns the distance between two coordinates or
// returns null if there is something wrong with them
function getDistance(coords1, coords2) {
  if (!coords1 || !coords2) return null;

  const { lat: lat1, lng: lng1 } = coords1;
  const { lat: lat2, lng: lng2 } = coords2;

  if (!lat2 || !lng1 || !lat2 || !lng2) return null;

  if (lat1 === lat2 && lng1 === lng2) {
    return 0;
  } else {
    const radlat1 = (Math.PI * lat1) / 180;
    const radlat2 = (Math.PI * lat2) / 180;
    const theta = lng1 - lng2;
    const radtheta = (Math.PI * theta) / 180;

    let dist =
      Math.sin(radlat1) * Math.sin(radlat2) +
      Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);

    if (dist > 1) dist = 1;

    dist = Math.acos(dist);
    dist = (dist * 180) / Math.PI;
    dist = dist * 60 * 1.1515;

    return dist;
  }
}

// Material design ripple effect
function addRipple(e, duration = 550, element, elOffset, preventRemove) {
  if (!e || !e.pageX) return;

  if (!element) {
    element = $(e.target);
  }

  const rippleContainer = document.createElement('div');
  rippleContainer.classList.add('ripple-container');

  let offset;

  if (!elOffset) {
    element[0].appendChild(rippleContainer);
    offset = element[0].getBoundingClientRect();
  } else {
    offset = elOffset;
  }

  const xPos = e.pageX - offset.left;
  const yPos = e.pageY - offset.top;

  const ripple = document.createElement('div');
  ripple.classList.add('ripple');
  ripple.style.left = `${xPos}px`;
  ripple.style.top = `${yPos}px`;
  ripple.style.animationDuration = `${duration}ms`;

  const size = element[0].offsetWidth + xPos;
  const adjustment = -(size / 2);

  ripple.style.width = `${size}px`;
  ripple.style.height = `${size}px`;
  ripple.style.marginLeft = `${adjustment}px`;
  ripple.style.marginTop = `${adjustment}px`;

  if (elOffset) {
    element[0].appendChild(rippleContainer);
  }

  rippleContainer.appendChild(ripple);

  if (!preventRemove) {
    ripple.addEventListener('animationend', () => {
      rippleContainer.parentNode.removeChild(rippleContainer);
    });
  }
}

function clearSelection() {
  if (window.getSelection) {
    const sel = window.getSelection();

    if (sel.rangeCount > 0 && sel.getRangeAt(0).getClientRects().length > 0) {
      sel.removeAllRanges();
    }
  } else if (document.selection) {
    document.selection.empty();
  }
}
